import { useMemo, useState } from 'react'
import { Download, Plus, Search, Trash2 } from 'lucide-react'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  Countdown,
  DocumentPreviewModal,
  Empty,
  FilterRow,
  InfoGrid,
  InfoItem,
  Input,
  MatriculeTag,
  Modal,
  PageHeader,
  Pagination,
  SegmentedPills,
  Select,
  Stat,
  StatGrid,
  StatusBadge,
  Table,
  Tabs,
  Textarea,
  useTheme,
  useToast,
  type Column,
  type SortState,
} from '@concours/ui'

/**
 * Vitrine du design system (DESIGN.md) : chaque composant dans ses états,
 * pour la recette visuelle en thème clair et sombre. Données fictives.
 */

interface DemoRow {
  id: string
  matricule: string
  nom: string
  concours: string
  statut: 'submitted' | 'under_review' | 'to_complete' | 'admissible' | 'not_admissible'
  paiement: 'confirmed' | 'pending' | 'failed'
  note: number | null
}

const DEMO_ROWS: DemoRow[] = [
  {
    id: '1',
    matricule: 'PEN-2026-A7K9Q2',
    nom: 'Awa Diop',
    concours: 'Agents pénitentiaires',
    statut: 'admissible',
    paiement: 'confirmed',
    note: 14.25,
  },
  {
    id: '2',
    matricule: 'PEN-2026-M3XR8T',
    nom: 'Moussa Ndiaye',
    concours: 'Agents pénitentiaires',
    statut: 'under_review',
    paiement: 'confirmed',
    note: null,
  },
  {
    id: '3',
    matricule: 'PEN-2026-Q8ZK4N',
    nom: 'Fatou Sall',
    concours: 'Contrôleurs',
    statut: 'to_complete',
    paiement: 'pending',
    note: null,
  },
  {
    id: '4',
    matricule: 'PEN-2026-H2WD9P',
    nom: 'Ibrahima Fall',
    concours: 'Contrôleurs',
    statut: 'not_admissible',
    paiement: 'failed',
    note: 8.5,
  },
  {
    id: '5',
    matricule: 'PEN-2026-T6VB3S',
    nom: 'Mariama Ba',
    concours: 'Agents pénitentiaires',
    statut: 'submitted',
    paiement: 'confirmed',
    note: 16,
  },
]

const IN_FIVE_DAYS = new Date(Date.now() + 5 * 86_400_000 + 3 * 3_600_000).toISOString()
const IN_TWENTY_HOURS = new Date(Date.now() + 20 * 3_600_000).toISOString()

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader title={title} />
      <CardBody>{children}</CardBody>
    </Card>
  )
}

export function DesignPage() {
  const toast = useToast()
  const { resolved } = useTheme()
  const [tab, setTab] = useState('boutons')
  const [period, setPeriod] = useState<'jour' | 'semaine' | 'mois'>('semaine')
  const [sort, setSort] = useState<SortState>({ key: 'nom', direction: 'asc' })
  const [page, setPage] = useState(3)
  const [modalOpen, setModalOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [loadingTable, setLoadingTable] = useState(false)

  const sortedRows = useMemo(() => {
    const factor = sort.direction === 'asc' ? 1 : -1
    return [...DEMO_ROWS].sort(
      (a, b) =>
        String(a[sort.key as keyof DemoRow] ?? '').localeCompare(
          String(b[sort.key as keyof DemoRow] ?? '')
        ) * factor
    )
  }, [sort])

  const previewBlob = useMemo(
    () =>
      new Blob(
        [
          `<!doctype html><meta charset="utf-8"><body style="font-family:Georgia,serif;padding:48px"><h1>Récépissé de dépôt</h1><p>Matricule de candidature : <b>PEN-2026-A7K9Q2</b></p><p>Document fictif de démonstration.</p></body>`,
        ],
        { type: 'text/html' }
      ),
    []
  )

  const columns: Column<DemoRow>[] = [
    {
      key: 'matricule',
      header: 'Matricule',
      sortable: true,
      cell: (r) => <MatriculeTag kind="candidature" value={r.matricule} copyable={false} />,
    },
    { key: 'nom', header: 'Candidat', sortable: true },
    { key: 'concours', header: 'Concours', sortable: true },
    {
      key: 'statut',
      header: 'Dossier',
      cell: (r) => <StatusBadge domain="application" status={r.statut} />,
    },
    {
      key: 'paiement',
      header: 'Paiement',
      cell: (r) => <StatusBadge domain="payment" status={r.paiement} />,
    },
    {
      key: 'note',
      header: 'Moyenne',
      numeric: true,
      cell: (r) => (r.note === null ? '—' : r.note.toFixed(2)),
    },
  ]

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <PageHeader
        eyebrow="DESIGN.md"
        title="Design system"
        description={`Composants et tokens partagés par les deux portails. Thème actuel : ${resolved === 'dark' ? 'sombre' : 'clair'} — bouton en haut à droite pour basculer.`}
        actions={
          <>
            <Button icon={<Download />}>Exporter</Button>
            <Button variant="primary" icon={<Plus />}>
              Nouveau concours
            </Button>
          </>
        }
      />

      <Section title="Couleurs (tokens)">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
          {[
            'paper',
            'paper-2',
            'paper-3',
            'card',
            'rule',
            'rule-2',
            'ink',
            'ink-2',
            'ink-3',
            'ink-4',
            'terra',
            'terra-deep',
            'terra-soft',
            'brand-deep',
            'flag-green',
            'flag-yellow',
            'flag-red',
            'sahel',
            'indigo',
            'success',
            'warning',
            'danger',
            'info',
          ].map((token) => (
            <div key={token}>
              <div
                className="h-12 rounded-md border border-rule"
                style={{ background: `var(--${token})` }}
              />
              <div className="mono mt-1 text-xs text-ink-3">--{token}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Typographie">
        <div className="stack">
          <p className="font-display" style={{ fontSize: 'var(--text-3xl)', lineHeight: 1.1 }}>
            Concours d’agents pénitentiaires
          </p>
          <p className="font-display" style={{ fontSize: 'var(--text-xl)' }}>
            Montserrat — titres, valeurs, modales
          </p>
          <p>
            Inter — interface et texte courant. Les dossiers soumis sont verrouillés pour les
            modifications ordinaires.
          </p>
          <p className="mono">JetBrains Mono — PEN-2026-A7K9Q2 · 15 000 FCFA · CAN-7K9Q2A4M</p>
          <p className="text-muted">Texte secondaire (--ink-3, contraste AA).</p>
        </div>
      </Section>

      <Tabs
        label="Rubriques"
        value={tab}
        onChange={setTab}
        items={[
          { value: 'boutons', label: 'Boutons et badges' },
          { value: 'formulaires', label: 'Formulaires' },
          { value: 'retours', label: 'Retours et modales' },
        ]}
      />

      {tab === 'boutons' && (
        <>
          <Section title="Boutons">
            <div className="stack">
              <div className="row">
                <Button variant="primary">Primaire</Button>
                <Button>Secondaire</Button>
                <Button variant="accent">Accent</Button>
                <Button variant="ghost">Discret</Button>
                <Button variant="danger" icon={<Trash2 />}>
                  Supprimer
                </Button>
                <Button variant="primary" loading>
                  Envoi…
                </Button>
                <Button disabled>Désactivé</Button>
                <Button iconOnly icon={<Search />} aria-label="Rechercher" />
              </div>
              <div className="row">
                <Button size="sm" variant="primary">
                  Petit
                </Button>
                <Button size="sm">Petit</Button>
                <Button size="lg" variant="primary">
                  Grand
                </Button>
              </div>
            </div>
          </Section>
          <Section title="Badges et statuts">
            <div className="stack">
              <div className="row">
                {(
                  [
                    'success',
                    'warning',
                    'danger',
                    'info',
                    'neutral',
                    'accent',
                    'outline',
                    'sahel',
                    'indigo',
                  ] as const
                ).map((v) => (
                  <Badge key={v} variant={v}>
                    {v}
                  </Badge>
                ))}
              </div>
              <div className="row">
                <StatusBadge domain="competition" status="draft" />
                <StatusBadge domain="competition" status="published" />
                <StatusBadge domain="competition" status="open" />
                <StatusBadge domain="competition" status="suspended" />
                <StatusBadge domain="competition" status="closed" />
                <StatusBadge domain="competition" status="provisional_results" />
                <StatusBadge domain="competition" status="final_results" />
              </div>
              <div className="row">
                <MatriculeTag kind="utilisateur" value="CAN-7K9Q2A4M" />
                <MatriculeTag kind="candidature" value="PEN-2026-A7K9Q2" />
                <Countdown deadline={IN_FIVE_DAYS} />
                <Countdown deadline={IN_TWENTY_HOURS} />
                <Countdown deadline="2020-01-01T00:00:00Z" />
              </div>
              <SegmentedPills
                label="Période"
                value={period}
                onChange={setPeriod}
                options={[
                  { value: 'jour', label: 'Jour' },
                  { value: 'semaine', label: 'Semaine' },
                  { value: 'mois', label: 'Mois' },
                ]}
              />
            </div>
          </Section>
        </>
      )}

      {tab === 'formulaires' && (
        <Section title="Champs">
          <div className="grid-2">
            <Input
              label="Nom"
              required
              placeholder="Ex. Diop"
              hint="Tel qu’indiqué sur la pièce d’identité."
            />
            <Input
              label="Courriel"
              type="email"
              defaultValue="awa.diop@"
              error="Adresse électronique invalide."
            />
            <Select
              label="Niveau de diplôme"
              required
              placeholder="Choisir…"
              options={[
                { value: 'bac', label: 'Baccalauréat' },
                { value: 'licence', label: 'Licence' },
                { value: 'master', label: 'Master' },
              ]}
            />
            <Input
              label="Matricule de candidature"
              mono
              readOnly
              value="PEN-2026-A7K9Q2"
              hint="Généré automatiquement — lecture seule."
            />
            <Input label="Désactivé" disabled value="Non modifiable après soumission" />
            <Textarea label="Observations" placeholder="Texte long…" />
          </div>
        </Section>
      )}

      {tab === 'retours' && (
        <Section title="Alertes, notifications et modales">
          <div className="stack">
            <Alert tone="info" title="Dépôts ouverts">
              Les candidatures sont acceptées jusqu’au 15 décembre à 23 h 59.
            </Alert>
            <Alert tone="success" title="Dossier soumis">
              Votre récépissé est disponible.
            </Alert>
            <Alert tone="warning" title="Complément demandé">
              Merci de fournir une copie lisible du diplôme.
            </Alert>
            <Alert tone="danger" title="Paiement échoué">
              La transaction a été refusée par l’opérateur.
            </Alert>
            <div className="row">
              <Button
                onClick={() =>
                  toast.success('Concours publié', 'L’avis est désormais visible du public.')
                }
              >
                Toast succès
              </Button>
              <Button
                onClick={() => toast.error('Échec de l’envoi', 'Réessayez dans quelques instants.')}
              >
                Toast erreur
              </Button>
              <Button onClick={() => setModalOpen(true)}>Modale</Button>
              <Button variant="danger" onClick={() => setConfirmOpen(true)}>
                Confirmation avec motif
              </Button>
              <Button onClick={() => setPreviewOpen(true)}>Aperçu de document</Button>
            </div>
          </div>
        </Section>
      )}

      <StatGrid>
        <Stat label="Candidatures soumises" value="4 812" hint="+312 cette semaine" />
        <Stat label="Recevables" value="3 990" hint="82,9 % des dossiers instruits" />
        <Stat label="Paiements confirmés" value="4 655" />
        <Stat label="Places" value="250" hint="Agents pénitentiaires — session 2026" />
      </StatGrid>

      <Card>
        <CardHeader
          title="Tableau"
          actions={
            <Button size="sm" onClick={() => setLoadingTable((v) => !v)}>
              {loadingTable ? 'Afficher les données' : 'Simuler le chargement'}
            </Button>
          }
        />
        <FilterRow>
          <Input label="Recherche" placeholder="Nom ou matricule" fieldClassName="flex-1" />
          <Select
            label="Statut"
            placeholder="Tous"
            options={[
              { value: 'submitted', label: 'Soumis' },
              { value: 'admissible', label: 'Recevable' },
            ]}
          />
        </FilterRow>
        <Table
          columns={columns}
          rows={sortedRows}
          rowKey={(r) => r.id}
          sort={sort}
          onSortChange={setSort}
          loading={loadingTable}
          caption="Candidatures (démonstration)"
        />
        <Pagination page={page} perPage={25} total={4812} onPageChange={setPage} />
      </Card>

      <Card>
        <CardHeader title="Fiche (libellé / valeur) et état vide" />
        <CardBody>
          <InfoGrid>
            <InfoItem label="Candidat">Awa Diop</InfoItem>
            <InfoItem label="Matricule utilisateur">
              <MatriculeTag kind="utilisateur" value="CAN-7K9Q2A4M" />
            </InfoItem>
            <InfoItem label="Date de dépôt">12 novembre 2026, 10 h 42</InfoItem>
            <InfoItem label="Frais">
              <span className="mono">15 000 FCFA</span>
            </InfoItem>
          </InfoGrid>
        </CardBody>
        <Empty
          title="Aucune convocation"
          description="Les convocations apparaîtront ici après validation de l’étape."
        />
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Prolonger les dépôts"
        description="Concours d’agents pénitentiaires — session 2026"
        footer={
          <>
            <Button variant="ghost" onClick={() => setModalOpen(false)}>
              Annuler
            </Button>
            <Button variant="primary" onClick={() => setModalOpen(false)}>
              Enregistrer
            </Button>
          </>
        }
      >
        <div className="stack">
          <Input label="Nouvelle date de clôture" type="datetime-local" required />
          <Textarea label="Motif" required hint="Conservé dans l’historique du concours." />
        </div>
      </Modal>

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={(reason) => {
          setConfirmOpen(false)
          toast.success('Concours suspendu', `Motif : ${reason}`)
        }}
        title="Suspendre le concours ?"
        message="Les dépôts seront bloqués jusqu’à la reprise. Les candidats en cours de saisie en seront informés."
        confirmLabel="Suspendre"
        tone="danger"
        reasonRequired
      />

      <DocumentPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title="Récépissé de dépôt"
        blob={previewBlob}
        fileName="recepisse.html"
      />
    </div>
  )
}
